import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Modal from '@/presentation/modules/procedures/components/Modal/Modal';
import ContentModalOneButton from '@/presentation/modules/procedures/components/ContentModalOneButton';
import routes from '@/presentation/components/Routes/routingMap';
import constants from '@/core/constants';
import useExternalSettings from '@/presentation/hooks/useExternalSettings';

const OVERLAY_ID = 'profile-photo-overlay';

const applyOverlayStyles = (el: HTMLElement) => {
  Object.assign(el.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    zIndex: '2147483646',
    display: 'block',
    pointerEvents: 'none',
    background: 'transparent'
  } as CSSStyleDeclaration);
};

const isOverlayCompromised = (el: HTMLElement) => {
  const cs = getComputedStyle(el);
  return (
    cs.display === 'none' ||
    cs.visibility !== 'visible' ||
    cs.pointerEvents === 'none' ||
    cs.position !== 'fixed' ||
    cs.opacity === '0'
  );
};

const ensureOverlay = (): HTMLElement => {
  let el = document.getElementById(OVERLAY_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = OVERLAY_ID;
    el.tabIndex = 0;
    document.body.appendChild(el);
  }
  applyOverlayStyles(el);
  return el;
};

const ProfilePhotoRestrictionModal = () => {
  const { restrictStudentPhoto } = useExternalSettings();
  const profilePhoto = useSelector((state: any) => state?.profilePhoto?.data);

  const history = useHistory();
  const hasPhoto = !!profilePhoto && profilePhoto !== constants.DEFAULT_PHOTO;
  const showModal = (restrictStudentPhoto ?? false) && !hasPhoto;

  useEffect(() => {
    if (!showModal) {
      const existing = document.getElementById(OVERLAY_ID);
      if (existing) existing.remove();
      document.body.style.overflow = '';
      return () => {};
    }

    ensureOverlay();
    const getActiveLightBoxes = () =>
      Array.from(
        document.querySelectorAll('[data-testid="cmp-LightBox"].active')
      ) as HTMLElement[];

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const stopAll = (e: Event) => {
      const lightBoxes = getActiveLightBoxes();
      if (!lightBoxes.length) return;
      const isInside = lightBoxes.some((lb) => lb.contains(e.target as Node));
      if (!isInside) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    const keyGuard = (e: KeyboardEvent) => {
      if (
        ['Tab', 'Escape', 'F12', 'R'].includes(e.key) ||
        e.ctrlKey ||
        e.metaKey
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
      const lightBoxes = getActiveLightBoxes();
      const activeEl = document.activeElement as HTMLElement | null;
      const targetLB =
        (activeEl && lightBoxes.find((lb) => lb.contains(activeEl))) ||
        lightBoxes[0];
      if (targetLB) {
        if (targetLB.tabIndex < 0) targetLB.tabIndex = 0;
        if (!activeEl || !targetLB.contains(activeEl)) {
          targetLB.focus({ preventScroll: true });
        }
      }
    };

    const handleContextMenu = (e: any) => {
      e.preventDefault();
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('mousedown', stopAll, true);
    document.addEventListener('mouseup', stopAll, true);
    document.addEventListener('touchstart', stopAll, true);
    document.addEventListener('touchend', stopAll, true);
    document.addEventListener('keydown', keyGuard, true);
    const lbs = getActiveLightBoxes();
    if (lbs[0]) {
      if (lbs[0].tabIndex < 0) lbs[0].tabIndex = 0;
      lbs[0].focus({ preventScroll: true });
    }

    const repair = () => {
      const current = document.getElementById(OVERLAY_ID);
      if (!current) {
        ensureOverlay().focus({ preventScroll: true });
        return;
      }
      if (isOverlayCompromised(current)) {
        applyOverlayStyles(current);
      }
    };

    const mo = new MutationObserver(() => repair());
    mo.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true
    });

    let rafId = 0;
    const loop = () => {
      repair();
      rafId = window.requestAnimationFrame(loop);
    };
    rafId = window.requestAnimationFrame(loop);

    return () => {
      mo.disconnect();
      window.cancelAnimationFrame(rafId);
      document.removeEventListener('mousedown', stopAll, true);
      document.removeEventListener('mouseup', stopAll, true);
      document.removeEventListener('touchstart', stopAll, true);
      document.removeEventListener('touchend', stopAll, true);
      document.removeEventListener('keydown', keyGuard, true);
      document.removeEventListener('contextmenu', handleContextMenu);
      const existing = document.getElementById(OVERLAY_ID);
      if (existing) existing.remove();
      document.body.style.overflow = prevOverflow;
    };
  }, [showModal]);

  return (
    <Modal show={showModal}>
      <ContentModalOneButton
        title="Realiza el registro de tu foto para acceder a este servicio"
        showInfo={false}
        textButton="Registrar mi foto"
        center
        onClickButton={() =>
          history.push(
            routes.student.procedures.categories.requests.updatephoto
              .procedureinfo
          )
        }
      >
        <p className="contentSubtitle">
          Esta restricción es por tu seguridad y para validar tu identidad.
        </p>
        <p style={{ marginTop: '2rem' }} className="contentSubtitle">
          Revisa los requisitos para registrar tu foto de manera inmediata.
        </p>
      </ContentModalOneButton>
    </Modal>
  );
};

export default ProfilePhotoRestrictionModal;
